// @flow
import React, {Component, Fragment} from 'react';
import Helmet from 'react-helmet-async';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import gql from 'graphql-tag';
import {Waypoint} from '@homeaway/react-unveil';
import Experiment from '@homeaway/react-experiment';
import DatesPromptBanner from '../DatesPromptBanner/DatesPromptBanner';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Hit, {fragments as hitFragments} from '../Hit/Hit';
import PinnedProperty, {
    fragments as PinnedPropertyFragment
} from '../PinnedProperty/PinnedProperty';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import ScrollListener from '../ScrollListener/ScrollListener';
import {isDateless} from '../../utils/searchParamsUtils';
import {isDifferent} from '../../utils';
import Timing from '../Timing/Timing';
import {XS_MIN} from '../../utils/breakpoints';

import type {
    SERPHitShape as HitSERPHitShape,
    ListingShape as HitListingShape
} from '../Hit/Hit';
import type {SERPHitShape as PinnedPropertySERPHitShape} from '../PinnedProperty/PinnedProperty';
import withPAR from '../Timing/withPAR';
import {triggerListHitClickEDAPEvent} from '../../utils/hitEdapUtils';
import {HA_SERP_DATES_PROMPT} from '../../abacus/experiments';

import type {Breakpoint} from '../../utils/breakpoints';
import type {State as ReduxState} from '../../reducers';

import './HitCollection.less';
import {parseHitUnveilClass} from './hitCollectionUtils';

export const fragments = {
    results: gql`
        fragment HitCollectionResults on Results {
            hitCollection {
                hits {
                    ...PinnedPropertyHit
                    ...HitSERPHit
                }
                from
            }
            listings {
                ...HitListing
            }
        }
        ${PinnedPropertyFragment.PinnedPropertyHit}
        ${hitFragments.SERPHit}
        ${hitFragments.listing}
    `
};
export type SERPHitShape = HitSERPHitShape & PinnedPropertySERPHitShape;
export type ListingShape = HitListingShape;
type StateProps = {
    breakpoint: Breakpoint,
    fetchImages?: string,
    directEntry: boolean,
    shouldShowDatesPromptBanner: boolean
};

type OwnProps = {
    results: {
        hitCollection: {
            hits?: SERPHitShape[],
            from?: number
        },
        listings?: ListingShape[]
    },
    onScrollHitEnter: (number) => void,
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: () => void
};

export type Props = OwnProps & StateProps;

export const NoResults = () => (
    <div className="NoResults">
        <FormattedMessage
            id="noresults"
            defaultMessage="There are no results which match your criteria. Try refining your search parameters."
        />
    </div>
);
NoResults.displayName = 'NoResults';

const hasNoPrice = (hit: SERPHitShape): 1 | 0 => {
    return hit && !(hit.price && hit.price.currencyUnits && hit.price.value)
        ? 1
        : 0;
};

export const checkPriceless = (hits?: SERPHitShape[]) => {
    if (!hits) {
        return;
    }
    let numPriceless = 0;
    hits.map((h: SERPHitShape) => {
        numPriceless += hasNoPrice(h);
    });
    const pricelesslabel: string = numPriceless.toString();
    if (numPriceless > 0) {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'no price presented',
            highfrequency: 'true',
            eventlabel: pricelesslabel
        });
    }
};

export class HitCollection extends Component<Props> {
    constructor(props: Props) {
        super(props);
        const {
            results: {
                hitCollection: {hits}
            }
        } = props;
        checkPriceless(hits);
    }

    componentDidUpdate(prevProps: Props) {
        const {
            results: {
                hitCollection: {hits: prevHits}
            }
        } = prevProps;
        const {
            results: {
                hitCollection: {hits}
            }
        } = this.props;

        if (prevHits && hits && isDifferent(hits, prevHits)) {
            checkPriceless(hits);
        }
    }

    render() {
        const {
            directEntry,
            onHitMouseEnter,
            onHitMouseLeave,
            onScrollHitEnter,
            results: {
                hitCollection: {hits, from},
                listings
            },
            shouldShowDatesPromptBanner,
            breakpoint
        } = this.props;

        const handleEnter = (label: number) => {
            onScrollHitEnter(label);
        };

        if (!hits || !hits.length || !listings || !listings.length) {
            return <NoResults />;
        }

        const handleScroll = () => handleEnter(0);
        return (
            <Timing measureName="hitCollection">
                <ScrollListener
                    useWindowAsScrollParent
                    onScroll={handleScroll}
                    data-wdio="ScrollListener"
                >
                    <div
                        className="HitCollection"
                        data-wdio="HitCollection"
                        role="list"
                    >
                        {hits.map((hit, index) => {
                            const listing = listings[index];
                            const absoluteIndex = index + from - 1;
                            const hitProps = {
                                key: listing.listingId,
                                hit: filterGraphQlAnywhere(
                                    hitFragments.SERPHit,
                                    hit
                                ),
                                onHitClick: triggerListHitClickEDAPEvent,
                                onMouseEnter: onHitMouseEnter,
                                onMouseLeave: onHitMouseLeave,
                                index: absoluteIndex,
                                listing
                            };
                            if (hit.pinned) {
                                return (
                                    <PinnedProperty
                                        key={index}
                                        available={hit.available}
                                        numResults={hits.length}
                                    >
                                        <Hit {...hitProps} />
                                    </PinnedProperty>
                                );
                            }

                            if ([4, 9].includes(index)) {
                                return (
                                    <Waypoint
                                        data-wdio={`Waypoint${index + 1}`}
                                        key={index}
                                        label={index + 1}
                                        onEnter={handleEnter}
                                        useWindowAsScrollParent
                                    >
                                        <Hit {...hitProps} />
                                    </Waypoint>
                                );
                            }
                            if (index === 7) {
                                return (
                                    <Fragment key={index}>
                                        <Hit {...hitProps} />
                                        {shouldShowDatesPromptBanner && (
                                            <Experiment
                                                name={HA_SERP_DATES_PROMPT}
                                            >
                                                {({Variant}) => {
                                                    const banner = (
                                                        <DatesPromptBanner
                                                            useXsImage={
                                                                breakpoint ===
                                                                XS_MIN
                                                            }
                                                        />
                                                    );

                                                    return (
                                                        <Fragment>
                                                            <Variant
                                                                variant={1}
                                                            >
                                                                {banner}
                                                            </Variant>
                                                            <Variant
                                                                variant={2}
                                                            >
                                                                {!directEntry &&
                                                                    banner}
                                                            </Variant>
                                                        </Fragment>
                                                    );
                                                }}
                                            </Experiment>
                                        )}
                                    </Fragment>
                                );
                            }
                            return <Hit {...hitProps} />;
                        })}
                    </div>
                </ScrollListener>
            </Timing>
        );
    }
}

const mapStateToProps = ({
    searchParams,
    viewport: {breakpoint},
    system: {directEntry}
}: ReduxState): StateProps => {
    return {
        directEntry,
        fetchImages:
            searchParams &&
            searchParams.params &&
            searchParams.params.fetchImages,
        breakpoint,
        shouldShowDatesPromptBanner: isDateless(searchParams.params)
    };
};

export const ConnectedHitCollection = compose(
    connect(mapStateToProps),
    withPAR
)(HitCollection);

const HitCollectionWithHelmet = (props: OwnProps) => {
    const {
        results: {
            hitCollection: {hits}
        }
    } = props;
    return (
        <Fragment>
            {hits && (
                <Helmet>
                    <style>
                        {hits
                            .slice(0, 3)
                            .filter((hit) => !!(hit.images && hit.images[0]))
                            .map((hit, index) => {
                                const url = hit.images[0].lowResSrc;
                                return url
                                    ? `.${parseHitUnveilClass(
                                          index
                                      )} {background-image: url("${url}");}`
                                    : '';
                            })
                            .join(' ')}
                    </style>
                </Helmet>
            )}
            <ConnectedHitCollection {...props} />
        </Fragment>
    );
};

export default HitCollectionWithHelmet;
